import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtDateDMY } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { UserPlus, XCircle } from "lucide-react";

export default function Invites() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({ email: "", role: "filler", name: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [i, r] = await Promise.all([api.get("/team/invites"), api.get("/team/roles")]);
    setRows(i.data);
    setRoles(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { await api.post("/team/invites", form); toast.success(`Invite sent to ${form.email}`); setForm({ email: "", role: "filler", name: "", notes: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const revoke = async (id) => {
    try { await api.post(`/team/invites/${id}/revoke`); toast.success("Revoked"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="invites-root">
      <Toaster position="top-center" />
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Invites</h1>
        <p className="text-sm text-slate-600 mt-1">Pre-approve an email for a role. When they sign in with Google, they're auto-assigned that role.</p>
      </div>

      <Card className="p-4 border-2 border-slate-300 rounded-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Email *</Label>
            <Input data-testid="invite-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="filler@example.com" className="h-10 mt-1 border border-slate-300" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Role *</Label>
            <select data-testid="invite-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 mt-1 border border-slate-300 rounded-sm px-2 bg-white">
              {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Preview name (optional)" className="h-10 mt-1 border border-slate-300" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Shift / joining day / etc" className="h-10 mt-1 border border-slate-300" />
          </div>
        </div>
        <Button data-testid="create-invite-btn" onClick={create} disabled={busy || !form.email} className="mt-3 bg-slate-900 hover:bg-slate-800 text-white">
          <UserPlus size={14} className="mr-1" /> Send invite
        </Button>
        <p className="text-xs text-slate-500 mt-2">
          Share this URL with them: <b>{window.location.origin}/login</b> — they sign in with Google using the invited email; role is applied automatically.
        </p>
      </Card>

      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <table className="dense-table w-full text-sm">
          <thead><tr><th>Email</th><th>Role</th><th>Name</th><th>Status</th><th>Invited by</th><th>When</th><th></th></tr></thead>
          <tbody>
            {rows.map((i) => (
              <tr key={i.invite_id} data-testid={`invite-row-${i.invite_id}`}>
                <td className="font-mono text-xs">{i.email}</td>
                <td><span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border state-info">{i.role}</span></td>
                <td>{i.name}</td>
                <td>
                  <span className={"text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm border " + (i.status === "pending" ? "state-warn" : i.status === "accepted" ? "state-ok" : "state-danger")}>
                    {i.status}
                  </span>
                </td>
                <td className="text-xs">{i.invited_by}</td>
                <td className="text-xs">{fmtDateDMY(i.created_at)}</td>
                <td>
                  {i.status === "pending" && (
                    <Button size="sm" variant="outline" onClick={() => revoke(i.invite_id)} className="text-xs h-7 border-slate-300" data-testid={`revoke-${i.invite_id}`}>
                      <XCircle size={12} className="mr-1" /> Revoke
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
